import _ from 'lodash'
import Backbone from 'backbone'
import template from './data-used-in-calculations.hbs'
import enums from 'scoreboard/components/enums'
import equationHighlighter from 'scoreboard/components/equations/equation-highlighter'
import equationParser from '../metric-equation-parser'
import calendars from 'scoreboard/components/calendars'
import Tooltip from 'scoreboard/components/tooltip'
import formatValue from 'scoreboard/components/format-value'
import i18n from 'scoreboard/components/i18n'
import conductor from 'scoreboard/components/conductor'
import unwrapModel from 'scoreboard/components/unwrap-model'
import guard from 'scoreboard/components/unsaved/guard'

var DataUsedInCalculations = Backbone.View.extend({

	className: 'data-used-in-calculations',

	/**
	 * The data used in calculations dialog, which is launched from the
	 * Scorecards Overview
	 *
	 * @constructs
	 *
	 * @param {Object} options
	 *
	 * @param {ScorecardNode} options.model the ScorecardNode we're working with
	 *
	 * @param {String} options.seriesKey the resource bundle key for the series name
	 *
	 * @param {Object} options.series the series we're working with
	 *
	 * @param {Number} options.periodId the calendar period ID
	 *
	 * @param {Object} options.data information about the data used in
	 * calculations (see the DataUsedInCalculations Java class)
	 *
	 * @param {String} options.result the result of evaluating the equation
	 */
	initialize: function(options) {
		this.options = options || {}
	},

	events: {
		'mouseenter .reference': 'onMouseEnter',
		'mouseleave .reference': 'onMouseLeave',
		'click .reference': 'onClick'
	},

	render: function() {
		this.$el.empty().append(template(this.context()))
		this.renderValues()
		return this
	},

	renderValues: function() {
		let parsedEquation = equationParser.parse(this.options.series.equation)
		this.$('.values').html(this.$('.equation').html())
		_.each(this.$('.values .reference'), function(el, index) {
			let reference = parsedEquation[index]
			let dataType = reference.type === 'score' ? 'score' : 'number'
			let domainObject
			if (reference.type === 'initiative') {
				domainObject = this.options.data.initiatives[reference.id]
				dataType = this.getCalculationDataType(reference.calculation)
			} else {
				if (reference.id) {
					domainObject = this.options.data.scorecardItems[reference.id]
				} else {
					domainObject = unwrapModel(this.model)
				}
				// #26555 Scores are always numeric
				// (e.g. yes-no score of 10 should not become 'Yes')
				if (reference.type !== 'score') {
					dataType = domainObject.dataType
				}
			}
			let currencyCode = domainObject.currencyCode
			const value = _.get(this.options, `data.values.${index}`)
			let formattedValue = this.formatValue({ value, dataType, currencyCode })
			this.$(el).text(formattedValue)
		}.bind(this))
	},

	formatValue: function(options = {}) {
		let value = options.value
		if (_.isNil(value)) {
			return i18n.get('common.blank')
		} else if (value === 'not-applicable') {
			return i18n.get('scorecards.node.metric.notapplicable')
		} else {
			return formatValue(_.defaults({}, options, {
				dataType: this.model.get('dataType'),
				currencyCode: this.model.get('currencyCode'),
				precision: this.model.get('precision'),
				value
			}))
		}
	},

	/**
	 * Returns the data type for an initiative calculation mentioned in an
	 * equation.  For example, 'TD' stands for total budget and has dataType
	 * 'currency'
	 */
	getCalculationDataType(calculation) {
		let calcsByType = {
			currency: [
				'initiatives.calculation.budget.acronym',
				'initiatives.calculation.budgetSpentToDate.acronym',
				'initiatives.calculation.projectedBudget.acronym',
				'initiatives.calculation.projectedBudgetVariance.acronym',
				'initiatives.calculation.budgetRemaining.acronym'
			],
			percent: [
				'initiatives.calculation.percentComplete.acronym',
				'initiatives.calculation.projectedBudgetVariancePercent.acronym',
				'initiatives.calculation.percentTimeElapsed.acronym'
			],
			number: [
				'initiatives.calculation.projectedScheduleVariance.acronym',
				'initiatives.calculation.daysElapsed.acronym'
			]
		}

		for (let dataType in calcsByType) {
			let i18nCodes = calcsByType[dataType]
			for (let i=0; i<i18nCodes.length; i++) {
				if (i18n.get(i18nCodes[i]) === calculation) {
					return dataType
				}
			}
		}

		throw new Error('Unknown calculation ' + calculation)
	},

	context: function() {
		var series = this.options.series
		var equation = equationHighlighter.highlight(equationParser, series.equation).equation
		// Get rid of whitespace before and after <span> references for even spacing styling
		equation = equation.replace(/>\s/g,'>') // catches </span> +<span>
		equation = equation.replace(/\s</g,'<') // catches </span>+ <span>
		var missingValueTreatment = enums.missingValueTreatment[series.missingValueTreatment]
		var period = calendars.periodsById[this.options.data.scoreInPeriodId]
		var result = _.isEmpty(_.trim(this.options.result)) ?
			i18n.get('common.blank') : this.options.result
		return {
			missingValueTreatment,
			equation,
			seriesKey: this.options.seriesKey,
			pluralSeriesKey: this.options.seriesKey + '.plural',
			period: period.label,
			result
		}
	},

	/**
	 * When hovering over a reference in an equation or hovering over a number
	 * that a reference evaluates to.
	 */
	onMouseEnter: function(e) {
		// whether we're hovering over the equation (vs hovering over the
		// values)
		var equation = this.$(e.target).closest('.equation').length === 1
		var index = this.$(e.target).index()

		// highlight the hovered over reference
		var hoveredSelector = equation ? '.equation' : '.values'
		this.$(hoveredSelector + ' .reference').eq(index)
			.addClass('text-link-emphasized is-highlighted')

		// highlight the reference in the opposite box
		var oppositeSelector = equation ? '.values' : '.equation'
		this.$(oppositeSelector + ' .reference').eq(index).addClass('text-link-emphasized')

		// show a tooltip with the domain object's name

		var data = this.$(e.target).data()
		var id = parseInt(data.id) || this.model.id
		var domainObjectContainer = data.type === 'initiative' ? 'initiatives' :
			'scorecardItems'
		var label
		if (domainObjectContainer === 'scorecardItems' && id == this.model.id) {
			label = this.model.get('label')
		} else {
			label = this.options.data[domainObjectContainer][id].label
		}

		// place the tooltip away from the other series of references so the
		// tooltip doesn't obscure the corresponding value in the equation
		// or values box
		var placement = equation ? 'above' : 'below'

		this._tooltip = new Tooltip({
			attachTo: e.target,
			content: label,
			placement,
			arrow: 'center',
			ignoreAttachToClick: true,
			extraClass: 'data-used-in-calculations-tooltip'
		}).render()
	},

	/**
	 * When no longer hovering over a reference or number that a reference
	 * evalutes to.
	 */
	onMouseLeave: function(e) {
		this.removeTooltip()
		this.$('.text-link-emphasized').removeClass('text-link-emphasized is-highlighted')
	},

	onClick: function(e) {
		var data = this.$(e.currentTarget).data()
		var section = data.type === 'initiative' ? 'initiatives' : 'scorecards'
		if (data.id == 'undefined') { // #54182
			data.id = this.model.id
		}

		// #61478 could be in the middle of approving metric changes, so need
		// to check the guard
		guard.conditionallyRun(() => {
			conductor.overview(section, data.id)
			this.trigger('done')
		})
	},

	removeTooltip: function() {
		_.invoke(this._tooltip, 'remove')
	},

	remove: function() {
		Backbone.View.prototype.remove.apply(this, arguments)
		this.removeTooltip()
	}

})

export default DataUsedInCalculations

DataUsedInCalculations.__filename__ = 'scorecards/overview/data-used-in-calculations.js'
